import {
  EdgeProps,
  Edge as FlowEdge,
  Node as FlowNode,
  getSmoothStepPath,
  getStraightPath,
} from '@xyflow/react';
import React from 'react';

import type { WorkflowCardData, WorkflowMode } from './types';

export type WorkflowEdgeData = {
  dashArray?: string;
  animatedDots?: {
    color: string;
    duration?: number;
    delay?: number;
    opacity?: number;
  }[];
  useStraight?: boolean;
  customPath?: string;
  borderRadius?: number;
  splitAtTrunk?: boolean;
  pathOptions?: {
    borderRadius?: number;
    offset?: number;
  };
};

export type WorkflowEdge = FlowEdge<WorkflowEdgeData>;

export const WorkflowEdgeComponent: React.FC<EdgeProps<WorkflowEdge>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}) => {
  const { useStraight, customPath, borderRadius, splitAtTrunk, pathOptions } =
    data ?? {};
  let [path] = useStraight
    ? getStraightPath({ sourceX, sourceY, targetX, targetY })
    : getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        borderRadius: pathOptions?.borderRadius ?? borderRadius,
        offset: pathOptions?.offset,
      });
  if (splitAtTrunk && !useStraight && !pathOptions) {
    const direction = targetY >= sourceY ? 1 : -1;
    const radius = Math.min(20, Math.abs(targetX - sourceX) / 2, 40);
    const trunkX = targetX - radius;
    const verticalEndY = targetY - direction * radius;
    const angleDeterminer = 0.5522847498;
    const control1Y = verticalEndY + direction * radius * angleDeterminer;
    const control2X = targetX - radius * angleDeterminer;

    path = `M ${sourceX},${sourceY} L ${trunkX},${sourceY} L ${trunkX},${verticalEndY} C ${trunkX},${control1Y} ${control2X},${targetY} ${targetX},${targetY}`;
  }

  const pathValue = customPath ?? path;

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={pathValue}
        style={style}
        markerEnd={markerEnd}
      />
      {data?.animatedDots?.map((dot, index) => (
        <circle
          key={`${id}-dot-${index}`}
          r={4}
          fill={dot.color}
          opacity={dot.opacity ?? 1}
          style={{ pointerEvents: 'none' }}>
          <animateMotion
            dur={`${dot.duration ?? 1.5}s`}
            repeatCount="indefinite"
            path={pathValue}
            begin={dot.delay ? `${dot.delay}s` : undefined}
          />
        </circle>
      ))}
    </>
  );
};

interface BuildEdgesArgs {
  nodes: FlowNode[];
  cards: WorkflowCardData[];
  mode: WorkflowMode;
  isLoadingLyrics: boolean;
  isProducing: boolean;
  songsReady: boolean;
  stackedView: boolean;
  isStationMode?: boolean;
}

const EDGE_STYLE: React.CSSProperties = {
  stroke: 'rgba(255,255,255,0.6)',
  strokeWidth: 4,
};

const findNode = (nodes: FlowNode[], id: string) =>
  nodes.find((node) => node.id === id);

export const buildWorkflowEdges = ({
  nodes,
  cards,
  mode,
  isLoadingLyrics,
  isProducing,
  songsReady,
  stackedView,
  isStationMode = false,
}: BuildEdgesArgs): WorkflowEdge[] => {
  const isImageMode = mode === 'image';
  const useHorizontalHandles = !stackedView;

  const avatarHandle = stackedView ? 'bottom' : 'right';
  const lyricsTarget = useHorizontalHandles ? 'left' : 'top';
  const lyricsSource = useHorizontalHandles ? 'right' : 'bottom';
  const produceTarget = useHorizontalHandles ? 'left' : 'top';
  const produceSource = stackedView ? 'bottom' : 'right';
  const cardTarget = stackedView ? 'top' : 'left';

  const edges: WorkflowEdge[] = [];
  const avatarNode = findNode(nodes, 'avatar');
  const card1Node = findNode(nodes, 'card1');
  const card2Node = findNode(nodes, 'card2');
  const lyricsNode = findNode(nodes, 'lyrics');
  const produceNode = findNode(nodes, 'produce');

  const pushEdge = (edge: WorkflowEdge) => {
    edges.push({
      ...edge,
      type: edge.type ?? 'workflow',
      style: { ...EDGE_STYLE, ...(edge.style ?? {}) },
    });
  };

  const card1Data = cards.find((card) => card.id === 'card1');
  const card2Data = cards.find((card) => card.id === 'card2');

  if (isImageMode) {
    // image mode, edges connect from avatar to cards (skip in Station mode)
    if (card1Node && !isStationMode && avatarNode) {
      pushEdge({
        id: 'avatar-card1',
        source: 'avatar',
        target: 'card1',
        sourceHandle: avatarHandle,
        targetHandle: cardTarget,
        data: {
          borderRadius: stackedView ? 160 : 48,
          splitAtTrunk: !stackedView,
          pathOptions: !stackedView
            ? {
                borderRadius: 40,
                offset: 40,
              }
            : undefined,
          animatedDots:
            isProducing && !(card1Data?.isReady ?? false)
              ? [
                  { color: 'rgba(255,255,255,0.9)', duration: 1.5 },
                  {
                    color: 'rgba(255,255,255,0.7)',
                    duration: 1.5,
                    delay: 0.5,
                  },
                ]
              : undefined,
        },
      });
    }

    if (card2Node && !isStationMode && avatarNode) {
      pushEdge({
        id: 'avatar-card2',
        source: 'avatar',
        target: 'card2',
        sourceHandle: avatarHandle,
        targetHandle: cardTarget,
        data: {
          borderRadius: stackedView ? 160 : 48,
          splitAtTrunk: !stackedView,
          pathOptions: !stackedView
            ? {
                borderRadius: 40,
                offset: 40,
              }
            : undefined,
          animatedDots:
            isProducing && !(card2Data?.isReady ?? false)
              ? [
                  { color: 'rgba(255,255,255,0.9)', duration: 1.5 },
                  {
                    color: 'rgba(255,255,255,0.7)',
                    duration: 1.5,
                    delay: 0.5,
                  },
                ]
              : undefined,
        },
      });
    }

    return edges;
  }

  // music mode, connect avatar -> lyrics -> produce -> cards (skip in Station mode)
  if (lyricsNode && !isStationMode && avatarNode) {
    pushEdge({
      id: 'avatar-lyrics',
      source: 'avatar',
      target: 'lyrics',
      sourceHandle: avatarHandle,
      targetHandle: lyricsTarget,
      data: {
        useStraight: true,
        animatedDots: isLoadingLyrics
          ? [{ color: 'rgba(255,255,255,0.9)', duration: 1.5 }]
          : undefined,
        borderRadius: stackedView ? 8 : 40,
      },
    });
  }

  if (lyricsNode && produceNode) {
    pushEdge({
      id: 'lyrics-produce',
      source: 'lyrics',
      target: 'produce',
      sourceHandle: lyricsSource,
      targetHandle: produceTarget,
      data: {
        useStraight: true,
        animatedDots:
          isProducing && !songsReady
            ? [
                { color: 'rgba(255,255,255,0.9)', duration: 1.2 },
                {
                  color: 'rgba(255,255,255,0.7)',
                  duration: 1.2,
                  delay: 0.4,
                },
              ]
            : undefined,
        borderRadius: stackedView ? 8 : 40,
      },
    });
  }

  if (produceNode && card1Node && card1Data) {
    pushEdge({
      id: 'produce-card1',
      source: 'produce',
      target: 'card1',
      sourceHandle: produceSource,
      targetHandle: cardTarget,
      data: {
        animatedDots:
          isProducing && !songsReady
            ? [
                { color: 'rgba(255,255,255,0.9)', duration: 1.5 },
                {
                  color: 'rgba(255,255,255,0.7)',
                  duration: 1.5,
                  delay: 0.5,
                },
              ]
            : undefined,
        borderRadius: stackedView ? 160 : 48,
        splitAtTrunk: !stackedView,
      },
    });
  }

  if (produceNode && card2Node && card2Data) {
    pushEdge({
      id: 'produce-card2',
      source: 'produce',
      target: 'card2',
      sourceHandle: produceSource,
      targetHandle: cardTarget,
      data: {
        animatedDots:
          isProducing && !songsReady
            ? [
                { color: 'rgba(255,255,255,0.9)', duration: 1.5 },
                {
                  color: 'rgba(255,255,255,0.7)',
                  duration: 1.5,
                  delay: 0.5,
                },
              ]
            : undefined,
        borderRadius: stackedView ? 160 : 48,
        splitAtTrunk: !stackedView,
      },
    });
  }

  return edges;
};
